import React, { useState, useEffect } from 'react';
import Card from '../../components/common/Card/Card';
import Button from '../../components/common/Button/Button';
import Modal from '../../components/common/Modal/Modal';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import { getMyStudentClasses, getMyStudentProjects } from '../../services/studentService';
import { formatDate, formatTime } from '../../utils/helpers';
import styles from './StudentClasses.module.css';
import { sortProjectsByName } from '../../utils/sortProjects';

const StudentClasses = () => {
  const [classes, setClasses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('all');
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'upcoming', 'past'
  const [showLogModal, setShowLogModal] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [classesData, projectsData] = await Promise.all([
        getMyStudentClasses(),
        getMyStudentProjects()
      ]);
      setClasses(classesData);
      setProjects(projectsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredClasses = classes.filter(cls => {
    // Filter by project
    if (selectedProject !== 'all' && cls.project_id !== selectedProject) {
      return false;
    }
    
    // Filter by date
    const classDate = new Date(cls.class_date);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    classDate.setHours(0, 0, 0, 0);
    
    if (filter === 'upcoming') {
      return classDate >= now;
    } else if (filter === 'past') {
      return classDate < now;
    }
    return true;
  });

  const getClassStatus = (cls) => {
    const classDate = new Date(cls.class_date);
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    classDate.setHours(0, 0, 0, 0);
    
    const isPast = classDate < currentDate;
    
    if (!isPast) {
      return { color: 'empty', icon: '○', label: 'Upcoming' };
    }
    
    // Class date has passed
    if (cls.mentor_attendance && cls.student_attendance) {
      return { color: 'green', icon: '✓', label: 'Both attended' };
    } else if (cls.mentor_attendance && !cls.student_attendance) {
      return { color: 'blue', icon: '✓', label: 'Only mentor attended' };
    } else if (!cls.mentor_attendance && cls.student_attendance) {
      return { color: 'orange', icon: '✓', label: 'Only student attended' };
    } else {
      return { color: 'grey', icon: '✓', label: 'Class held (not logged)' };
    }
  };

  const isClassPast = (classDate) => {
    const cls = new Date(classDate);
    const current = new Date();
    current.setHours(0, 0, 0, 0);
    cls.setHours(0, 0, 0, 0);
    return cls < current;
  };

  const handleViewLog = (cls) => {
    setSelectedClass(cls);
    setShowLogModal(true);
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <LoadingSpinner size="large" message="Loading classes..." />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>My Classes</h1>
        <div className={styles.headerControls}>
          <div className={styles.projectSelector}>
            <label htmlFor="projectSelect">Project:</label>
            <select
              id="projectSelect"
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className={styles.projectSelect}
            >
              <option value="all">All Projects</option>
              {sortProjectsByName(projects).map(project => (
                <option key={project.id} value={project.id}>
                  {project.project_name}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.filterButtons}>
            <button
              className={`${styles.filterBtn} ${filter === 'all' ? styles.active : ''}`}
              onClick={() => setFilter('all')}
            >
              All
            </button>
            <button
              className={`${styles.filterBtn} ${filter === 'upcoming' ? styles.active : ''}`}
              onClick={() => setFilter('upcoming')}
            >
              Upcoming
            </button>
            <button
              className={`${styles.filterBtn} ${filter === 'past' ? styles.active : ''}`}
              onClick={() => setFilter('past')}
            >
              Past
            </button>
          </div>
        </div>
      </div>

      {filteredClasses.length === 0 ? (
        <Card>
          <div className={styles.empty}>
            <p>No classes found</p>
          </div>
        </Card>
      ) : (
        <div className={styles.classesGrid}>
          {filteredClasses.map(cls => {
            const status = getClassStatus(cls);
            const isPast = isClassPast(cls.class_date);
            
            return (
              <Card 
                key={cls.id} 
                hover 
                className={`${styles.classCard} ${isPast ? styles.pastClass : ''}`}
              >
                <div className={styles.classHeader}>
                  <div className={styles.classTitle}>
                    <span 
                      className={`${styles.statusIndicator} ${styles[`status${status.color.charAt(0).toUpperCase() + status.color.slice(1)}`]}`}
                      title={status.label}
                    >
                      {status.icon}
                    </span>
                    <h3>Class {cls.class_number}</h3>
                  </div>
                  <span className={styles.duration}>{cls.duration} min</span>
                </div>
                
                <div className={styles.classDetails}>
                  <p><strong>Date:</strong> {formatDate(cls.class_date)}</p>
                  <p><strong>Time:</strong> {formatTime(cls.class_time)}</p>
                  
                  {cls.meet_link && (
                    isPast ? (
                      <span className={`${styles.meetLink} ${styles.meetLinkDisabled}`}>
                         Join Meet
                      </span>
                    ) : (
                      <a 
                        href={cls.meet_link} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className={styles.meetLink}
                      >
                         Join Meet
                      </a>
                    )
                  )}
                  
                  <div className={styles.attendance}>
                    <span>Mentor: {cls.mentor_attendance ? 'Yes' : 'No'}</span>
                    <span>Student: {cls.student_attendance ? 'Yes' : 'No'}</span>
                  </div>
                </div>
                
                {cls.class_summary && (
                  <Button size="small" 
                    fullWidth 
                    variant="outline"
                    onClick={() => handleViewLog(cls)} loading={loading}
                  >
                    View Logs
                  </Button>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Read-only Log Modal for Students */}
      <Modal 
        isOpen={showLogModal} 
        onClose={() => setShowLogModal(false)} 
        title="Class Log Details"
      >
        {selectedClass && (
          <div className={styles.logModalContent}>
            <div className={styles.logSection}>
              <h4 className={styles.logSectionTitle}>Class Information</h4>
              <div className={styles.logDetail}>
                <span className={styles.logLabel}>Class Number:</span>
                <span className={styles.logValue}>Class {selectedClass.class_number}</span>
              </div>
              <div className={styles.logDetail}>
                <span className={styles.logLabel}>Date:</span>
                <span className={styles.logValue}>{formatDate(selectedClass.class_date)}</span>
              </div>
              <div className={styles.logDetail}>
                <span className={styles.logLabel}>Time:</span>
                <span className={styles.logValue}>{formatTime(selectedClass.class_time)}</span>
              </div>
              <div className={styles.logDetail}>
                <span className={styles.logLabel}>Duration:</span>
                <span className={styles.logValue}>{selectedClass.duration} minutes</span>
              </div>
            </div>

            <div className={styles.logSection}>
              <h4 className={styles.logSectionTitle}>Attendance</h4>
              <div className={styles.attendanceRow}>
                <span>Mentor: {selectedClass.mentor_attendance ? 'Attended' : 'Absent'}</span>
                <span>Student: {selectedClass.student_attendance ? 'Attended' : 'Absent'}</span>
              </div>
            </div>

            {selectedClass.topics_covered && selectedClass.topics_covered.length > 0 && (
              <div className={styles.logSection}>
                <h4 className={styles.logSectionTitle}>Topics Covered</h4>
                <ul className={styles.topicsList}>
                  {selectedClass.topics_covered.map((topic, idx) => (
                    <li key={idx}>{topic}</li>
                  ))}
                </ul>
              </div>
            )}

            {selectedClass.class_summary && (
              <div className={styles.logSection}>
                <h4 className={styles.logSectionTitle}>Class Summary</h4>
                <div className={styles.summaryBox}>
                  {selectedClass.class_summary}
                </div>
              </div>
            )}

            {selectedClass.resources_shared && selectedClass.resources_shared.length > 0 && (
              <div className={styles.logSection}>
                <h4 className={styles.logSectionTitle}>Resources Shared</h4>
                <ul className={styles.resourcesList}>
                  {selectedClass.resources_shared.map((resource, idx) => (
                    <li key={idx}>
                      <a href={resource} target="_blank" rel="noopener noreferrer" className={styles.resourceLink}>
                        Link: {resource}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {!selectedClass.class_summary && !selectedClass.topics_covered?.length && (
              <div className={styles.noLog}>
                <p>Note: No log has been added for this class yet.</p>
              </div>
            )}

            <div className={styles.modalActions}>
              <Button variant="primary" onClick={() => setShowLogModal(false)} loading={loading}>Close</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default StudentClasses;

